import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowRightIcon,
  HeartIcon,
  ClockIcon,
  UserGroupIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import DateScrollPicker from '../components/DateScrollPicker';
import { useGeolocation } from '../utils/location';
import AreaSearchModal from '../components/AreaSearchModal';
import JobCard from '../components/JobCard';
import JobCountdownTimer from '../components/JobCountdownTimer';
import HomepageBottomNavigation from '../components/HomepageBottomNavigation';
import BannerCarousel from '../components/BannerCarousel';
import { api, apiWithAuth } from '../lib/api';

// 勤務時間文字列から時間数を計算するヘルパー関数
const parseWorkHours = (workHours) => {
  if (!workHours) return 0;

  try {
    // "9:00-17:00" や "22:00-06:00" のような形式を想定
    const match = workHours.match(/(\d{1,2}):?(\d{0,2})\s*[-〜~]\s*(\d{1,2}):?(\d{0,2})/);
    if (match) {
      const startHour = parseInt(match[1]);
      const startMin = parseInt(match[2] || '0');
      const endHour = parseInt(match[3]);
      const endMin = parseInt(match[4] || '0');

      const startTime = startHour + startMin / 60;
      let endTime = endHour + endMin / 60;

      // 夜勤の場合（終了時刻が開始時刻より小さい場合）
      if (endTime < startTime) {
        endTime += 24; // 翌日の時刻として扱う
      }

      return Math.max(0, endTime - startTime);
    }
  } catch (error) {
    console.error('Error parsing work hours:', error);
  }

  return 0;
};

const HomePage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [employerProfiles, setEmployerProfiles] = useState([]);
  const [favoriteJobs, setFavoriteJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAreaModal, setShowAreaModal] = useState(false);

  // カレンダー関連の状態
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // 今日の日付をデフォルト
  });
  const [userLocation, setUserLocation] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  // 並び替え・フィルター関連の状態
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest', 'distance', 'rating'
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [originalProfiles, setOriginalProfiles] = useState([]); // フィルター前のデータを保持

  // 求職者ログイン後の求人・事業者表示関連の状態
  const [nearbyEmployers, setNearbyEmployers] = useState([]);
  const [locationPermission, setLocationPermission] = useState(null); // null, 'granted', 'denied'
  const [expiredJobIds, setExpiredJobIds] = useState(new Set()); // 期限切れ求人のID管理
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [loadingMoreEmployers, setLoadingMoreEmployers] = useState(false);

  const { latitude, longitude, error: locationError } = useGeolocation();

  // 期限切れ求人を処理する関数
  const handleJobExpired = (jobId) => {
    setExpiredJobIds(prev => new Set([...prev, jobId]));
  };

  // バナー取得
  const fetchBanners = async () => {
    try {
      const response = await api.get('/banners');
      if (response.status === 200) {
        const data = response.data;
        setBanners(data);
      }
    } catch (err) {
      console.error('Error fetching banners:', err);
    }
  };

  // カテゴリ取得
  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      if (response.status === 200) {
        const data = response.data;
        setCategories(data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // 事業者プロフィール（求人）取得
  const fetchEmployerProfiles = async (category = null, date = null) => {
    try {
      setLoading(true);

      // 事業者プロフィール専用のAPIエンドポイントを使用
      let url = '/employer-profiles';

      const params = new URLSearchParams();

      // 引数のカテゴリまたは選択されたカテゴリを使用
      const categoryToUse = category !== null ? category : selectedCategory;
      if (categoryToUse && categoryToUse !== '') {
        params.append('category', categoryToUse);
      }

      // 位置情報パラメータ（距離順ソートのため）
      if (latitude && longitude) {
        params.append('user_latitude', latitude.toString());
        params.append('user_longitude', longitude.toString());
        params.append('radius_km', '50'); // 50km範囲で検索
      }

      // 並び替え設定（デフォルトで距離順）
      const sortParam = sortOrder === 'newest' ? 'newest' :
        sortOrder === 'rating' ? 'hourly_rate' :
          sortOrder === 'distance' ? 'distance' : 'distance';
      params.append('sort_by', sortParam);

      // 日付パラメータ（将来の拡張用）
      if (date) {
        params.append('work_date', date);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await api.get(url.replace(api.defaults.baseURL, ''));
      if (response.status === 200) {
        const data = response.data;
        console.log('Employer profiles API response:', data);

        // 事業者プロフィールデータをそのまま使用
        let profiles = data;

        // データが配列でない場合の処理
        if (!Array.isArray(profiles)) {
          if (profiles.data) {
            profiles = profiles.data;
          } else {
            console.error('Unexpected response format:', profiles);
            profiles = [];
          }
        }

        // 期限切れ求人の除外
        profiles = profiles.filter(profile => !expiredJobIds.has(profile.id));

        // お気に入り状態を取得して設定
        const employersWithFavorites = await loadFavoriteStatus(profiles);
        setEmployerProfiles(employersWithFavorites);
        setOriginalProfiles(employersWithFavorites);
        setNearbyEmployers(employersWithFavorites); // nearbyEmployersも更新
      } else {
        setError('求人情報の取得に失敗しました');
      }
    } catch (err) {
      setError('ネットワークエラーが発生しました');
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  };



  // 位置情報が取得できた時の処理
  useEffect(() => {
    if (latitude && longitude && !userLocation) {
      fetchLocationName(latitude, longitude).then(locationName => {
        setUserLocation(locationName);
        console.log('Current location:', locationName);
      });
    }
  }, [latitude, longitude, userLocation]);

  // バナーのクリック数を記録
  const recordBannerClick = async (bannerId) => {
    try {
      await api.post(`/banners/${bannerId}/click`);
    } catch (err) {
      console.error('Error recording banner click:', err);
    }
  };

  // バナークリック処理
  const handleBannerClick = (banner) => {
    if (banner.link_url) {
      recordBannerClick(banner.id);
      window.open(banner.link_url, '_blank');
    }
  };

  // 日付選択処理
  const handleDateSelect = (date) => {
    setSelectedDate(date);

    // ログインしていない場合は求職者登録ページに遷移
    if (!user) {
      navigate('/register-jobseeker');
      return;
    }

    // 求職者の場合は位置情報ベースで事業者を取得
    if (user.user_type === 'job_seeker') {
      if (latitude && longitude) {
        fetchNearbyEmployers(latitude, longitude, selectedCategory, date);
      } else {
        // 位置情報がない場合は通常の検索
        fetchEmployerProfiles(selectedCategory, date);
      }
    }
  };

  // カテゴリ選択処理
  const handleCategorySelect = (category) => {
    // ログインしていない場合は求職者登録ページに遷移
    if (!user) {
      navigate('/register-jobseeker');
      return;
    }

    if (user && user.user_type === 'job_seeker') {
      // 求職者ログイン後は同じページで事業者をフィルタリング
      setSelectedCategory(category);

      // 選択されたカテゴリで事業者を再取得（位置情報ベース）
      if (latitude && longitude) {
        fetchNearbyEmployers(latitude, longitude, category, selectedDate);
      } else {
        // 位置情報がない場合は通常の事業者取得
        fetchEmployerProfiles(category, selectedDate);
      }
    } else {
      // 事業者またはその他の場合
      if (category === '') {
        // すべてのカテゴリ - フィルターをリセット
        setSelectedCategory('');
      } else {
        // 特定のカテゴリが選択された場合、カテゴリ別ページに遷移
        navigate(`/category/${encodeURIComponent(category)}`);
      }
    }
  };

  // 並び替え処理
  const sortProfiles = (profiles, order) => {
    const sortedProfiles = [...profiles];

    switch (order) {
      case 'distance':
        // 現地から近い順
        if (latitude && longitude) {
          return sortedProfiles.sort((a, b) => {
            const distanceA = a.distance_km || calculateDistance(latitude, longitude, a.latitude, a.longitude);
            const distanceB = b.distance_km || calculateDistance(latitude, longitude, b.latitude, b.longitude);
            return distanceA - distanceB;
          });
        } else {
          // GPS取得できない場合
          toast.error('位置情報を取得できませんでした');
          return sortedProfiles;
        }

      case 'rating':
        // 評価順（高評価順）
        return sortedProfiles.sort((a, b) => {
          const ratingA = a.rating || 0;
          const ratingB = b.rating || 0;
          return ratingB - ratingA; // 高評価順
        });

      case 'deadline':
        // 締切時刻が近い順
        return sortedProfiles.sort((a, b) => {
          const deadlineA = a.deadline ? new Date(a.deadline) : new Date('2099-12-31');
          const deadlineB = b.deadline ? new Date(b.deadline) : new Date('2099-12-31');
          return deadlineA - deadlineB;
        });

      case 'newest':
      default:
        // 新着順（登録日時順）
        return sortedProfiles.sort((a, b) => {
          const dateA = new Date(a.created_at || a.updated_at);
          const dateB = new Date(b.created_at || b.updated_at);
          return dateB - dateA; // 新しい順
        });
    }
  };

  // 並び替え実行
  const handleSort = (order) => {
    setSortOrder(order);

    if (order === 'combined' || order === 'distance' || order === 'newest' || order === 'rating') {
      // 新しいAPIを使用して再取得（リアルタイム並び替え）
      fetchEmployerProfiles();
    } else {
      // 既存のローカルソート（後方互換性）
      const sorted = sortProfiles(originalProfiles, order);
      setEmployerProfiles(sorted);
    }
  };

  // 位置情報が取得できた時の処理
  useEffect(() => {
    if (latitude && longitude && !userLocation) {
      fetchLocationName(latitude, longitude).then(locationName => {
        setUserLocation(locationName);
      });
    }
  }, [latitude, longitude, userLocation]);

  // お気に入り状態を取得
  const loadFavoriteStatus = async (employers) => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !user || user.user_type !== 'job_seeker') {
        return employers; // ログインしていない場合はそのまま返す
      }

      const authApi = apiWithAuth(token);
      const response = await authApi.get('/employer-favorites');
      if (response.status === 200) {
        const data = response.data;
        const favoriteIds = new Set(data.map(fav => fav.employer_id));

        return employers.map(emp => ({
          ...emp,
          is_favorite: favoriteIds.has(emp.employer_id || emp.id)
        }));
      }
    } catch (error) {
      console.error('Error loading favorite status:', error);
    }

    return employers;
  };

  // お気に入り事業者の切り替え
  const toggleEmployerFavorite = async (employerId) => {
    try {
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      const response = await authApi.post(`/employer-favorites/${employerId}`);

      if (response.status === 200) {
        const result = response.data;

        // nearbyEmployersとemployerProfiles両方を更新
        const updateEmployers = (prev) => prev.map(emp =>
          (emp.id === employerId || emp.employer_id === employerId)
            ? { ...emp, is_favorite: result.is_favorite }
            : emp
        );

        setNearbyEmployers(updateEmployers);
        setEmployerProfiles(updateEmployers);

        toast.success(result.message);
      }
    } catch (error) {
      console.error('Error toggling employer favorite:', error);
      toast.error('お気に入りの切り替えに失敗しました');
    }
  };

  // 求職者ログイン後の位置情報に基づく事業者取得
  const fetchNearbyEmployers = async (userLat, userLon, category = '', date = null) => {
    try {
      const params = new URLSearchParams({
        latitude: userLat.toString(),
        longitude: userLon.toString(),
        radius_km: '10'
      });

      if (category && category !== '') {
        params.append('category', category);
      }
      if (date) {
        params.append('work_date', date);
      }

      const url = `/employer-profiles?${params.toString()}`;
      const response = await fetch(url);

      if (response.ok) {
        const data = await response.json();
        // employer-profilesAPIの形式に合わせてマッピング
        const mappedData = data.map(profile => ({
          id: profile.id,
          profile_id: profile.id,  // プロフィールIDを追加
          employer_id: profile.employer_id,
          name: profile.employer_name,
          business_name: profile.business_name,
          title: profile.title,
          work_hours: profile.work_hours,
          hourly_rate: profile.hourly_rate,
          work_location: profile.work_location,
          description: profile.description,
          bio: profile.description,
          categories: profile.categories,
          location: profile.location,
          prefecture: profile.prefecture,
          city: profile.city,
          images: profile.images,
          videos: profile.videos,
          thumbnail_image: profile.thumbnail_image,
          tags: profile.tags,
          latitude: profile.latitude,
          longitude: profile.longitude,
          distance: profile.distance,
          avg_hourly_rate: profile.hourly_rate,
          active_jobs_count: 0, // デフォルト値
          is_favorite: false, // お気に入り状態は別途取得
          created_at: profile.created_at
        }));

        // お気に入り状態を取得して設定
        const employersWithFavorites = await loadFavoriteStatus(mappedData);
        setNearbyEmployers(employersWithFavorites);
      }
    } catch (error) {
      console.error('Error fetching nearby employers:', error);
      // エラー時は通常の事業者取得にフォールバック
      fetchEmployerProfiles(category);
    }
  };

  // もっと見るボタンのハンドラー
  const loadMoreEmployers = async () => {
    if (loadingMoreEmployers) return;

    try {
      setLoadingMoreEmployers(true);
      const currentCount = nearbyEmployers.length;
      const response = await api.get(`/search/employers?latitude=${latitude}&longitude=${longitude}&radius_km=10&offset=${currentCount}&limit=20`);

      if (response.status === 200) {
        const newEmployers = response.data;
        const employersWithDistance = newEmployers.map(employer => {
          if (employer.latitude && employer.longitude) {
            const distance = calculateDistance(latitude, longitude, employer.latitude, employer.longitude);
            return { ...employer, distance: distance.toFixed(1) };
          }
          return { ...employer, distance: null };
        });

        setNearbyEmployers(prev => [...prev, ...employersWithDistance]);
      }
    } catch (error) {
      console.error('Error loading more employers:', error);
      toast.error('追加の事業者の読み込みに失敗しました');
    } finally {
      setLoadingMoreEmployers(false);
    }
  };

  // 位置情報の許可を要求
  const requestLocationPermission = async () => {
    setLoadingLocation(true);

    if (!navigator.geolocation) {
      toast.error('このブラウザでは位置情報を取得できません');
      setLocationPermission('denied');
      setLoadingLocation(false);
      return;
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5分間キャッシュ
        });
      });

      const { latitude: lat, longitude: lon } = position.coords;
      setLocationPermission('granted');

      // 位置情報に基づく事業者を取得
      await fetchNearbyEmployers(lat, lon, selectedCategory, selectedDate);

      // 地名も更新
      const locationName = await fetchLocationName(lat, lon);
      setUserLocation(locationName);

      toast.success('現在地から近い求人を表示しています');
    } catch (error) {
      console.error('Error getting location:', error);
      setLocationPermission('denied');
      toast.error('位置情報の取得に失敗しました');

      // 位置情報取得失敗時は通常の求人表示
      fetchEmployerProfiles();
    } finally {
      setLoadingLocation(false);
    }
  };

  // 距離計算
  // 位置情報から地名を取得
  const fetchLocationName = async (lat, lon) => {
    try {
      const response = await api.get(`/location/reverse-geocode?lat=${lat}&lon=${lon}`);
      if (response.status === 200) {
        const data = response.data;
        return data.location_name || '登録住所';
      }
      return '登録住所';
    } catch (error) {
      console.error('Error fetching location name:', error);
      // ユーザーの登録住所を使用してフォールバック
      if (user && user.prefecture && user.city) {
        return `${user.prefecture.replace('都', '').replace('府', '').replace('県', '')}${user.city}`;
      }
      return 'エリア未登録';
    }
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat2 || !lon2) return Infinity;

    const R = 6371; // 地球の半径（km）
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // バナーカルーセル制御
  const nextBanner = () => {
    setCurrentBannerIndex((prevIndex) =>
      prevIndex === banners.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevBanner = () => {
    setCurrentBannerIndex((prevIndex) =>
      prevIndex === 0 ? banners.length - 1 : prevIndex - 1
    );
  };

  // 自動バナー切り替え
  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(nextBanner, 5000); // 5秒ごと
      return () => clearInterval(interval);
    }
  }, [banners]);

  // 初期データ取得
  useEffect(() => {
    fetchBanners();
    fetchCategories();

    console.log('HomePage: user状態:', user);

    // 求職者ログイン後の処理
    if (user && user.user_type === 'job_seeker') {
      console.log('HomePage: 求職者ユーザー情報:', {
        latitude: user.latitude,
        longitude: user.longitude,
        prefecture: user.prefecture,
        city: user.city
      });

      // ユーザーの登録住所に緯度・経度がある場合はそれを使用
      if (user.latitude && user.longitude) {
        console.log('HomePage: 緯度・経度を使用して位置名を取得');
        // 登録済みの位置情報に基づいて住所を取得
        fetchLocationName(user.latitude, user.longitude).then(locationName => {
          console.log('HomePage: 取得した位置名:', locationName);
          const finalLocationName = locationName || `${user.prefecture?.replace('都', '').replace('府', '').replace('県', '')}${user.city}`;
          console.log('HomePage: 最終的な位置名:', finalLocationName);
          setUserLocation(finalLocationName);
        });
        // 事業者情報を取得
        fetchNearbyEmployers(user.latitude, user.longitude, selectedCategory, selectedDate);
      } else if (user.prefecture && user.city) {
        console.log('HomePage: 都道府県・市区町村を使用');
        // 緯度・経度がない場合は住所文字列を直接使用
        const locationName = `${user.prefecture.replace('都', '').replace('府', '').replace('県', '')}${user.city}`;
        console.log('HomePage: 住所ベースの位置名:', locationName);
        setUserLocation(locationName);
        // 位置情報の許可を要求（現在地ベースの検索のため）
        requestLocationPermission();
      } else {
        console.log('HomePage: 位置情報が不完全、現在地許可を要求');
        // 位置情報の許可を要求
        requestLocationPermission();
      }
    }
  }, [user]);

  // 位置情報取得後の処理（現在地更新ボタン使用時など）
  useEffect(() => {
    if (latitude && longitude) {
      fetchLocationName(latitude, longitude).then(locationName => {
        setUserLocation(locationName);
      });

      // 求職者の場合は事業者情報を取得
      if (user && user.user_type === 'job_seeker') {
        fetchNearbyEmployers(latitude, longitude, selectedCategory, selectedDate);
      }
    }
  }, [latitude, longitude, user]);

  // 日付や位置情報、カテゴリ変更時の事業者プロフィール再取得
  useEffect(() => {
    // 求職者以外の場合のみ通常の事業者プロフィール取得
    if (!user || user.user_type !== 'job_seeker') {
      fetchEmployerProfiles();
    }
  }, [selectedDate, selectedCategory, latitude, longitude, user]);

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      {/* カレンダー日付選択エリア */}
      <DateScrollPicker
        selectedDate={selectedDate}
        onDateSelect={handleDateSelect}
        userLocation={userLocation}
      />

      {/* カルーセル広告エリア */}
      <section className="bg-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <BannerCarousel />
        </div>
      </section>

      {/* 事業者プロフィール表示セクション - ログイン前およびログイン済み事業者向け */}
      {(!user || user.user_type !== 'job_seeker') && (
        <section className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800 text-white overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="relative container mx-auto px-4 py-12 md:py-16 lg:py-20">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div className="animate-fadeIn text-center md:text-left">
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6 leading-tight">
                  今日から働ける
                  <br />
                  <span className="text-yellow-300">即日バイト</span>
                  <br />
                  見つけよう
                </h1>
                <p className="text-lg md:text-xl mb-6 md:mb-8 text-blue-100 max-w-lg mx-auto md:mx-0">
                  bytersなら画像・動画で職場の雰囲気が分かる！
                  <br />
                  業界最高水準の使いやすさで、理想のバイトが見つかります。
                </p>
                <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center md:justify-start">
                  <Link
                    to="/register/type"
                    className="bg-white text-blue-600 px-6 md:px-8 py-3 md:py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors btn-animate inline-flex items-center justify-center"
                  >
                    <MagnifyingGlassIcon className="w-5 h-5 mr-2" />
                    求人を探す
                  </Link>
                  <Link
                    to="/register/type"
                    className="bg-yellow-400 text-gray-900 px-6 md:px-8 py-3 md:py-4 rounded-lg font-semibold hover:bg-yellow-300 transition-colors btn-animate inline-flex items-center justify-center"
                  >
                    <MapPinIcon className="w-5 h-5 mr-2" />
                    活動エリアで探す
                  </Link>
                  {!user && (
                    <Link
                      to="/register/type"
                      className="border-2 border-white text-white px-6 md:px-8 py-3 md:py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors btn-animate inline-flex items-center justify-center"
                    >
                      無料で始める
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 事業者ログイン後の専用コンテンツ */}
      {user && user.user_type === 'employer' && (
        <section className="py-8 bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  事業者ダッシュボード
                </h2>
                <p className="text-gray-600">
                  求人投稿・応募管理・プロフィール設定などを行えます
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* 求人投稿 */}
                <div className="bg-blue-50 rounded-lg p-6 text-center">
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">求人投稿</h3>
                  <p className="text-gray-600 mb-4">新しい求人を簡単に投稿</p>
                  <Link
                    to="/create-job"
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    求人を投稿
                  </Link>
                </div>

                {/* 応募管理 */}
                <div className="bg-green-50 rounded-lg p-6 text-center">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <UserGroupIcon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">応募管理</h3>
                  <p className="text-gray-600 mb-4">応募者の管理・連絡</p>
                  <Link
                    to="/applications"
                    className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    応募を確認
                  </Link>
                </div>

                {/* プロフィール管理 */}
                <div className="bg-purple-50 rounded-lg p-6 text-center">
                  <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">プロフィール</h3>
                  <p className="text-gray-600 mb-4">企業情報の編集・管理</p>
                  <Link
                    to="/profile"
                    className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors"
                  >
                    プロフィール編集
                  </Link>
                </div>
              </div>
            </div>

            {/* 求人管理統計 */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">求人活動状況</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">-</div>
                  <div className="text-gray-600">掲載中の求人</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">-</div>
                  <div className="text-gray-600">今月の応募数</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">-</div>
                  <div className="text-gray-600">プロフィール閲覧</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">-</div>
                  <div className="text-gray-600">メッセージ</div>
                </div>
              </div>
            </div>

            {/* クイックアクション */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg text-white p-6">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold mb-2">理想の人材を見つけましょう</h3>
                  <p className="text-blue-100">bytersで優秀な求職者と出会えます</p>
                </div>
                <div className="mt-4 md:mt-0 flex space-x-4">
                  <Link
                    to="/employer-dashboard"
                    className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                  >
                    ダッシュボード
                  </Link>
                  <Link
                    to="/applications"
                    className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
                  >
                    求職者を探す
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 求職者ログイン後の位置情報ベース求人・事業者表示 */}
      {user && user.user_type === 'job_seeker' && (
        <section className="py-8 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* カテゴリ検索セクション */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">カテゴリから探す</h2>
              {categories && categories.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-4">
                  {/* すべてのカテゴリ */}
                  <button
                    onClick={() => handleCategorySelect('')}
                    className={`p-2 sm:p-4 rounded-lg border-2 transition-colors ${selectedCategory === ''
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                  >
                    <div className="text-lg sm:text-2xl mb-1 sm:mb-2"></div>
                    <div className="font-semibold text-xs sm:text-sm text-gray-900">すべて</div>
                    <div className="text-xs text-gray-500">
                      {categories.reduce((total, cat) => total + (cat.employer_count || 0), 0)}件
                    </div>
                  </button>

                  {/* 各カテゴリ */}
                  {categories.map((category) => (
                    <button
                      key={category.id || category.name}
                      onClick={() => handleCategorySelect(category.name)}
                      className={`p-2 sm:p-4 rounded-lg border-2 transition-colors ${selectedCategory === category.name
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                    >
                      <div className="text-lg sm:text-2xl mb-1 sm:mb-2">{category.icon}</div>
                      <div className="font-semibold text-xs sm:text-sm text-gray-900">{category.label || category.name}</div>
                      <div className="text-xs text-gray-500">{category.employer_count || 0}件</div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">カテゴリを読み込み中...</p>
                </div>
              )}
            </div>

            {/* 位置情報更新ボタン */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {userLocation ? `${userLocation}周辺の事業者` : '近くの事業者'}
              </h2>
              <button
                onClick={requestLocationPermission}
                disabled={loadingLocation}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center"
              >
                {loadingLocation ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    現在地取得中...
                  </>
                ) : (
                  <>
                    <MapPinIcon className="h-4 w-4 mr-2" />
                    現在地を更新
                  </>
                )}
              </button>
            </div>

            {/* 位置情報ステータス */}
            {locationPermission === 'denied' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-yellow-800 text-sm">
                  現在地が取得できませんでした。エリア設定に基づいて事業者を表示しています。
                  ブラウザの設定で位置情報を許可すると、より近い事業者を表示できます。
                </p>
              </div>
            )}

            {/* 事業者カード一覧（2列表示 - スマホでも2列維持） */}
            {nearbyEmployers.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                {nearbyEmployers.map((employer) => (
                  <div
                    key={employer.id}
                    className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    {/* 事業者サムネイル画像 */}
                    {employer.images && employer.images.length > 0 ? (
                      <div className="h-32 sm:h-48 bg-gray-200 relative">
                        <img
                          src={`data:image/jpeg;base64,${employer.images[0]}`}
                          alt={employer.business_name || employer.name}
                          className="w-full h-full object-cover"
                        />
                        {/* お気に入りボタン */}
                        <button
                          onClick={() => toggleEmployerFavorite(employer.id)}
                          className={`absolute top-1 right-1 sm:top-2 sm:right-2 p-1 sm:p-2 rounded-full ${employer.is_favorite
                            ? 'bg-red-500 text-white'
                            : 'bg-white bg-opacity-80 text-gray-600'
                            } hover:bg-opacity-100 transition-all`}
                        >
                          {employer.is_favorite ? (
                            <HeartIconSolid className="h-4 w-4 sm:h-5 sm:w-5" />
                          ) : (
                            <HeartIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                          )}
                        </button>

                        {/* 画像数インジケーター */}
                        {employer.images.length > 1 && (
                          <div className="absolute bottom-1 left-1 sm:bottom-2 sm:left-2 bg-black bg-opacity-50 text-white px-1 sm:px-2 py-1 rounded text-xs">
                            {employer.images.length}枚
                          </div>
                        )}

                        {/* 動画インジケーター */}
                        {employer.videos && employer.videos.length > 0 && (
                          <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 bg-black bg-opacity-50 text-white px-1 sm:px-2 py-1 rounded text-xs">
                            動画あり
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="h-32 sm:h-48 bg-gray-200 flex items-center justify-center relative">
                        <span className="text-gray-400 text-2xl sm:text-4xl"></span>
                        {/* お気に入りボタン */}
                        <button
                          onClick={() => toggleEmployerFavorite(employer.id)}
                          className={`absolute top-1 right-1 sm:top-2 sm:right-2 p-1 sm:p-2 rounded-full ${employer.is_favorite
                            ? 'bg-red-500 text-white'
                            : 'bg-white bg-opacity-80 text-gray-600'
                            } hover:bg-opacity-100 transition-all`}
                        >
                          {employer.is_favorite ? (
                            <HeartIconSolid className="h-4 w-4 sm:h-5 sm:w-5" />
                          ) : (
                            <HeartIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                          )}
                        </button>
                      </div>
                    )}

                    <div className="p-3 sm:p-4">
                      {/* タイトル（最大45文字表示） */}
                      {employer.title && (
                        <h3 className="font-bold text-sm sm:text-lg text-gray-900 mb-1 sm:mb-2 line-clamp-1">
                          {employer.title.length > 45 ? `${employer.title.substring(0, 45)}...` : employer.title}
                        </h3>
                      )}

                      {/* 事業者名（タイトルがない場合のフォールバック） */}
                      {!employer.title && (
                        <h3 className="font-bold text-sm sm:text-lg text-gray-900 mb-1 sm:mb-2 line-clamp-1">
                          {employer.business_name || employer.name}
                        </h3>
                      )}

                      {/* 勤務時間帯 */}
                      {employer.work_hours && (
                        <div className="flex items-center text-gray-600 text-xs sm:text-sm mb-1">
                          <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                          <span>{employer.work_hours}</span>
                        </div>
                      )}

                      {/* 勤務場所 */}
                      <div className="flex items-center text-gray-500 text-xs sm:text-sm mb-1 sm:mb-2">
                        <MapPinIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        <span className="truncate">
                          {user ?
                            // ログイン済みユーザーは自分の住所（都道府県+市区町村）を基準に表示
                            `${user.prefecture || ''}${user.city || ''}` :
                            // 未ログインユーザーは事業者の勤務場所をそのまま表示
                            (employer.work_location || employer.location)
                          }
                        </span>
                        {employer.distance && (
                          <span className="ml-1 sm:ml-2 bg-blue-100 text-blue-800 px-1 sm:px-2 py-1 rounded text-xs">
                            {employer.distance}km
                          </span>
                        )}
                      </div>

                      {/* 時給表示（概算給料削除、日給は小数点以下切り捨て） */}
                      {employer.hourly_rate && employer.work_hours && (
                        <div className="mb-1 sm:mb-2">
                          <div className="flex items-center justify-between">
                            <div className="text-sm sm:text-lg font-semibold text-green-600">
                              時給{Math.floor(employer.hourly_rate).toLocaleString()}
                            </div>
                            {(() => {
                              const hours = parseWorkHours(employer.work_hours);
                              const dailyEarnings = Math.floor(employer.hourly_rate * hours);
                              return (
                                <div className="text-xs sm:text-sm text-green-700">
                                  日給約{dailyEarnings.toLocaleString()}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      )}

                      {/* 平均時給表示（概算給料削除、日給は小数点以下切り捨て） */}
                      {!employer.hourly_rate && employer.avg_hourly_rate && employer.work_hours && (
                        <div className="mb-1 sm:mb-2">
                          <div className="flex items-center justify-between">
                            <div className="text-sm sm:text-lg font-semibold text-green-600">
                              平均{Math.floor(employer.avg_hourly_rate).toLocaleString()}
                            </div>
                            {(() => {
                              const hours = parseWorkHours(employer.work_hours);
                              const dailyEarnings = Math.floor(employer.avg_hourly_rate * hours);
                              return (
                                <div className="text-xs sm:text-sm text-green-700">
                                  日給約{dailyEarnings.toLocaleString()}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      )}

                      {/* 求人情報 */}
                      {employer.active_jobs_count > 0 && (
                        <div className="mb-1 sm:mb-2">
                          <div className="text-xs sm:text-sm text-gray-600">
                            求人 {employer.active_jobs_count}件
                          </div>
                        </div>
                      )}

                      {/* カテゴリタグ */}
                      {employer.categories && employer.categories.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2 sm:mb-3">
                          {employer.categories.slice(0, 2).map((cat, index) => (
                            <span
                              key={index}
                              className="bg-gray-100 text-gray-700 px-1 sm:px-2 py-1 rounded text-xs"
                            >
                              {categories.find(c => c.name === cat)?.label || cat}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* 詳細を見るボタン */}
                      <button
                        className="w-full bg-blue-500 text-white py-2 rounded-lg text-sm sm:text-base font-medium hover:bg-blue-600 transition-colors"
                        onClick={() => {
                          if (!user) {
                            // ログイン前の場合は求職者登録ページにリダイレクト
                            navigate('/register-jobseeker');
                          } else if (user.user_type === 'job_seeker') {
                            // 求職者ログイン後の場合は事業者詳細ページに遷移
                            navigate(`/employer-profile/${employer.profile_id || employer.id}`);
                          } else {
                            // 事業者ログインの場合は登録を促す
                            toast.error('事業者の詳細を見るには求職者としてログインしてください');
                          }
                        }}
                      >
                        詳細を見る
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4"></div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {locationPermission === 'denied' ? 'エリア内に事業者が見つかりません' : '近くに事業者が見つかりません'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {locationPermission === 'denied'
                    ? '位置情報を許可すると、より多くの事業者が表示される可能性があります。'
                    : '少し範囲を広げて検索してみてください。'
                  }
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={requestLocationPermission}
                    className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    現在地を更新
                  </button>
                  <Link
                    to="/mobile-search"
                    className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    詳細検索で探す
                  </Link>
                </div>
              </div>
            )}

            {/* 無限スクロール：もっと見るボタン */}
            {nearbyEmployers.length > 0 && nearbyEmployers.length >= 20 && (
              <div className="text-center mt-8">
                <button
                  onClick={loadMoreEmployers}
                  disabled={loadingMoreEmployers}
                  className="bg-blue-500 text-white px-8 py-3 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                >
                  {loadingMoreEmployers ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                      読み込み中...
                    </>
                  ) : (
                    'もっと見る'
                  )}
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* カテゴリセクション - ログイン前またはログイン済み事業者のみ表示 */}
      {(!user || user.user_type !== 'job_seeker') && (
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">カテゴリから探す</h2>
              <p className="text-gray-600">お仕事のジャンルから事業者を見つけましょう</p>
            </div>

            {categories && categories.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {/* すべてのカテゴリ */}
                <button
                  onClick={() => handleCategorySelect('')}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 ${selectedCategory === ''
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                >
                  <div className="text-2xl mb-2"></div>
                  <div className="font-medium text-sm">すべて</div>
                  <div className="text-xs text-gray-500">
                    {categories.reduce((total, cat) => total + (cat.employer_count || 0), 0)}件
                  </div>
                </button>

                {/* 各カテゴリ */}
                {categories.map((category) => (
                  <button
                    key={category.id || category.name}
                    onClick={() => handleCategorySelect(category.name)}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 ${selectedCategory === category.name
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    <div className="text-2xl mb-2">{category.icon || ''}</div>
                    <div className="font-medium text-sm">{category.label || category.name || 'カテゴリ'}</div>
                    <div className="text-xs text-gray-500">{category.employer_count || 0}件</div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-gray-500">カテゴリを読み込み中...</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* 事業者プロフィール一覧セクション - ログイン前またはログイン済み事業者のみ表示 */}
      {(!user || user.user_type !== 'job_seeker') && (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {selectedCategory
                  ? `${categories.find(c => c.name === selectedCategory)?.label || selectedCategory}の事業者`
                  : selectedDate === new Date().toISOString().split('T')[0]
                    ? '今日募集中の事業者'
                    : `${selectedDate}募集中の事業者`}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                {selectedCategory
                  ? `${categories.find(c => c.name === selectedCategory)?.label || selectedCategory}カテゴリの事業者一覧です。`
                  : 'お近くの事業者から、あなたにぴったりのお仕事を見つけましょう。'}
              </p>

              {/* フィルター表示 */}
              {(selectedCategory || selectedDate !== new Date().toISOString().split('T')[0]) && (
                <div className="flex flex-wrap justify-center gap-2 mt-4">
                  {selectedCategory && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                      カテゴリ: {categories.find(c => c.name === selectedCategory)?.label || selectedCategory}
                      <button
                        onClick={() => handleCategorySelect('')}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >

                      </button>
                    </span>
                  )}
                  {selectedDate !== new Date().toISOString().split('T')[0] && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                      日付: {selectedDate}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* 並び替えボタンとフィルターボタン */}
            <div className="bg-white rounded-lg p-4 shadow-sm mb-6">
              {/* デスクトップ・タブレット表示 */}
              <div className="hidden md:flex items-center justify-center gap-4">
                <button
                  onClick={() => handleSort('distance')}
                  className={`h-10 px-6 rounded-lg text-sm font-medium transition-colors whitespace-nowrap min-w-[120px] ${sortOrder === 'distance'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  現地から近い順
                </button>
                <button
                  onClick={() => handleSort('newest')}
                  className={`h-10 px-6 rounded-lg text-sm font-medium transition-colors whitespace-nowrap min-w-[80px] ${sortOrder === 'newest'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  新着順
                </button>
                <button
                  onClick={() => handleSort('rating')}
                  className={`h-10 px-6 rounded-lg text-sm font-medium transition-colors whitespace-nowrap min-w-[80px] ${sortOrder === 'rating'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  評価順
                </button>
                <button
                  onClick={() => setShowFilterModal(true)}
                  className="h-10 px-6 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors flex items-center space-x-2 min-w-[100px] justify-center"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
                  </svg>
                  <span>フィルター</span>
                </button>
              </div>

              {/* スマホ表示（2列レイアウト） */}
              <div className="md:hidden">
                <div className="grid grid-cols-2 gap-3">
                  {/* 上段 */}
                  <button
                    onClick={() => handleSort('distance')}
                    className={`h-10 px-4 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${sortOrder === 'distance'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    現地から近い順
                  </button>
                  <button
                    onClick={() => handleSort('newest')}
                    className={`h-10 px-4 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${sortOrder === 'newest'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    新着順
                  </button>

                  {/* 下段 */}
                  <button
                    onClick={() => handleSort('rating')}
                    className={`h-10 px-4 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${sortOrder === 'rating'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    評価順
                  </button>
                  <button
                    onClick={() => setShowFilterModal(true)}
                    className="h-10 px-4 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
                    </svg>
                    <span>フィルター</span>
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-gray-200 rounded-lg h-64 animate-pulse"></div>
                ))}
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-500 text-lg">{error}</p>
                <button
                  onClick={fetchEmployerProfiles}
                  className="mt-4 bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 transition-colors"
                >
                  再試行
                </button>
              </div>
            ) : employerProfiles.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">
                  {selectedCategory
                    ? `${categories.find(c => c.name === selectedCategory)?.label || selectedCategory}カテゴリの事業者が見つかりません`
                    : selectedDate === new Date().toISOString().split('T')[0]
                      ? '本日募集中の事業者はありません'
                      : `${selectedDate}に募集中の事業者はありません`}
                </p>
                <p className="text-gray-400 mt-2">
                  {selectedCategory
                    ? '他のカテゴリを選択してみてください'
                    : '別の日付を選択してみてください'}
                </p>
                {selectedCategory && (
                  <button
                    onClick={() => handleCategorySelect('')}
                    className="mt-4 text-blue-500 hover:text-blue-700 underline"
                  >
                    すべてのカテゴリを表示
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                {employerProfiles
                  .filter(profile => !expiredJobIds.has(profile.id)) // 期限切れ求人を除外
                  .map((profile) => (
                    <div
                      key={profile.id}
                      className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden cursor-pointer"
                      onClick={() => navigate(`/employer-profile/${profile.id}`)}
                    >
                      {/* 事業者プロフィール画像 */}
                      <div className="h-32 sm:h-48 bg-gray-200 relative overflow-hidden">
                        {profile.images && profile.images.length > 0 ? (
                          <img
                            src={profile.images[0]}
                            alt={profile.company_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center">
                            <UserGroupIcon className="h-8 w-8 sm:h-16 sm:w-16 text-gray-500" />
                          </div>
                        )}

                        {/* カウントダウンタイマー */}
                        <JobCountdownTimer
                          job={{
                            id: profile.id,
                            start_date: profile.created_at, // 仮の開始日（実際は今日の日付を使用）
                            work_hours: profile.work_hours
                          }}
                          onExpired={handleJobExpired}
                        />

                        {/* 距離表示 */}
                        {profile.distance && (
                          <div className="absolute top-2 right-2 bg-blue-500 text-white px-2 py-1 rounded text-xs">
                            {profile.distance}km
                          </div>
                        )}
                      </div>

                      <div className="p-3 sm:p-4">
                        {/* 事業者名 */}
                        <h3 className="font-bold text-sm sm:text-lg text-gray-900 mb-1 sm:mb-2">
                          {profile.business_name}
                        </h3>

                        {/* 勤務時間帯 */}
                        {profile.work_hours && (
                          <p className="text-blue-600 text-xs sm:text-sm mb-1 sm:mb-2 flex items-center">
                            {profile.work_hours}
                          </p>
                        )}

                        {/* 勤務場所 */}
                        {profile.work_location && (
                          <p className="text-gray-600 text-xs sm:text-sm mb-1 sm:mb-2">
                            {profile.work_location}
                          </p>
                        )}

                        {/* 時給 */}
                        {profile.hourly_rate && (
                          <p className="text-green-600 font-semibold text-sm sm:text-base mb-1 sm:mb-2">
                            時給 {profile.hourly_rate.toLocaleString()}
                          </p>
                        )}

                        {/* 給料表示（概算給料削除、日給は小数点以下切り捨て） */}
                        {profile.hourly_rate && profile.work_hours && (
                          (() => {
                            // 勤務時間から時間数を計算
                            const timeMatch = profile.work_hours.match(/(\d{1,2}):(\d{2})〜(\d{1,2}):(\d{2})/);
                            if (timeMatch) {
                              const startHour = parseInt(timeMatch[1]);
                              const startMin = parseInt(timeMatch[2]);
                              const endHour = parseInt(timeMatch[3]);
                              const endMin = parseInt(timeMatch[4]);

                              let totalHours = endHour - startHour;
                              let totalMins = endMin - startMin;

                              // 夜勤など、終了時刻が開始時刻より小さい場合の処理
                              if (totalHours < 0) {
                                totalHours += 24; // 翌日扱い
                              }

                              if (totalMins < 0) {
                                totalHours -= 1;
                                totalMins += 60;
                              }

                              const workHours = totalHours + (totalMins / 60);
                              const dailyEarnings = Math.floor(profile.hourly_rate * workHours);

                              return (
                                <div className="mb-1 sm:mb-2">
                                  <div className="flex items-center justify-between">
                                    <div className="text-sm sm:text-lg font-semibold text-green-600">
                                      時給{Math.floor(profile.hourly_rate).toLocaleString()}
                                    </div>
                                    <div className="text-xs sm:text-sm text-green-700">
                                      日給約{dailyEarnings.toLocaleString()}
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()
                        )}

                        {/* 募集期間 */}
                        {(profile.publication_start_date || profile.publication_end_date) && (
                          <div className="text-green-600 text-xs mb-1 sm:mb-2 flex items-center">
                            募集期間:
                            {profile.publication_start_date && new Date(profile.publication_start_date).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })}
                            {(profile.publication_start_date && profile.publication_end_date) && '〜'}
                            {profile.publication_end_date && new Date(profile.publication_end_date).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })}
                          </div>
                        )}

                        {/* カテゴリタグ */}
                        {profile.categories && profile.categories.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2 sm:mb-3">
                            {profile.categories.slice(0, 2).map((category, index) => (
                              <span
                                key={index}
                                className="bg-blue-100 text-blue-800 px-1 sm:px-2 py-1 rounded text-xs"
                              >
                                {categories.find(c => c.name === category)?.label || category}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* 説明文 */}
                        <p className="text-gray-600 text-xs sm:text-sm mb-2 sm:mb-3 line-clamp-2">
                          {profile.description}
                        </p>

                        {/* 詳細ボタン */}
                        <button
                          className="w-full bg-blue-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-blue-600 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!user) {
                              // ログイン前の場合は求職者登録ページにリダイレクト
                              navigate('/register-jobseeker');
                            } else if (user.user_type === 'job_seeker') {
                              // 求職者ログイン後の場合は事業者詳細ページに遷移
                              navigate(`/employer-profile/${profile.profile_id || profile.id}`);
                            } else {
                              // 事業者ログインの場合は登録を促す
                              toast.error('事業者の詳細を見るには求職者としてログインしてください');
                            }
                          }}
                        >
                          詳細を見る
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA Section - ログイン前または事業者ログイン時のみ表示 */}
      {(!user || user.user_type !== 'job_seeker') && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              今すぐ始めて理想のバイトを見つけよう
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              登録は無料、わずか1分で完了。今日から新しい働き方を始めませんか？
            </p>
            {!user ? (
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/register/type"
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors btn-animate"
                >
                  新規登録はこちら
                </Link>
              </div>
            ) : (
              <Link
                to="/jobs"
                className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors btn-animate inline-block"
              >
                求人を探す
              </Link>
            )}
          </div>
        </section>
      )}





      {/* フィルターモーダル */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 transform transition-all duration-300 ease-in-out">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">フィルター・並び替え</h2>

            <div className="space-y-3">
              <button
                onClick={() => {
                  handleSort('distance');
                  setShowFilterModal(false);
                }}
                className="w-full text-left px-4 py-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors flex items-center space-x-3"
              >
                <MapPinIcon className="h-5 w-5 text-gray-500" />
                <span className="font-medium text-gray-900">現在地から近い順</span>
              </button>

              <button
                onClick={() => {
                  handleSort('newest');
                  setShowFilterModal(false);
                }}
                className="w-full text-left px-4 py-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors flex items-center space-x-3"
              >
                <ClockIcon className="h-5 w-5 text-gray-500" />
                <span className="font-medium text-gray-900">新着順</span>
              </button>

              <button
                onClick={() => {
                  handleSort('rating');
                  setShowFilterModal(false);
                }}
                className="w-full text-left px-4 py-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors flex items-center space-x-3"
              >
                <StarIcon className="h-5 w-5 text-gray-500" />
                <span className="font-medium text-gray-900">時給が高い順</span>
              </button>

              <button
                onClick={() => {
                  handleSort('combined');
                  setShowFilterModal(false);
                }}
                className="w-full text-left px-4 py-3 rounded-lg bg-green-50 hover:bg-green-100 transition-colors flex items-center space-x-3 border-2 border-green-200"
              >
                <div className="flex items-center space-x-1">
                  <MapPinIcon className="h-4 w-4 text-green-600" />
                  <span className="text-green-600">+</span>
                  <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <div>
                  <span className="font-medium text-green-800">おすすめ: 近い+高時給</span>
                  <p className="text-xs text-green-600">10km以内で時給の高い順</p>
                </div>
              </button>

              <button
                onClick={() => {
                  setShowAreaModal(true);
                  setShowFilterModal(false);
                }}
                className="w-full text-left px-4 py-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors flex items-center space-x-3"
              >
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-500" />
                <span className="font-medium text-gray-900">エリア指定検索</span>
              </button>
            </div>

            <div className="mt-6 flex space-x-3">
              <button
                onClick={() => setShowFilterModal(false)}
                className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg font-medium hover:bg-gray-600 transition-colors"
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}

      {/* エリア検索モーダル */}
      <AreaSearchModal
        isOpen={showAreaModal}
        onClose={() => setShowAreaModal(false)}
      />

      {/* Homepage Bottom Navigation - 常時表示 */}
      <HomepageBottomNavigation />
    </div>
  );
};

export default HomePage;